import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import { AddImageIcon, DropDownBold, File } from "../AdminIcons";
import FormSelect from "../common/FormSelect";
import useClickOutsideModal from "../common/useClickOutsideModal";
import {
  useGetAllCategoriesQuery,
  useGetSingleCategoryAdminQuery,
} from "../../services/categories/categoriesApi";
import { useForm } from "react-hook-form";
import { useCreateCourseMutation } from "../../services/course/courseApi";
import { showToast } from "../../utils/toast";

const SELECT_FIELD_IDS = {
  selectedCategoryId: "course-category",
  selectedLevelId: "course-level",
  selectedSubCategoryId: "course-sub-category",
  selectedCourseType: "course-type",
  selectedAgeGroupId: "course-age-group",
};

const AddCourseModal = ({ isOpen, onClose }) => {
  const { data } = useGetAllCategoriesQuery();

  const categories = data?.data || [];

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState("");
  const [selectedCourseType, setSelectedCourseType] = useState("");

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [outlineFile, setOutlineFile] = useState(null);
  const [apiError, setApiError] = useState("");
  const [fieldError, setFieldError] = useState(null);

  const { register, getValues, reset } = useForm();

  const courseTitleField = register("courseTitle");
  const priceField = register("price");
  const accessDurationField = register("accessDuration");

  const fieldRefs = useRef({});
  const registerFieldRef = (field) => (element) => {
    fieldRefs.current[field] = element;
  };

  const clearFieldError = (field) => {
    setFieldError((current) => (current?.field === field ? null : current));
  };

  const focusField = (field) => {
    requestAnimationFrame(() => {
      const selectId = SELECT_FIELD_IDS[field];
      const element = selectId
        ? document.getElementById(selectId)
        : fieldRefs.current[field];

      if (!element) return;

      element.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof element.focus === "function") {
        element.focus({ preventScroll: true });
      }
    });
  };

  const getFirstFieldError = (values) => {
    if (!thumbnailFile) {
      return { field: "thumbnailFile", message: "Course thumbnail is required." };
    }
    if (!values.courseTitle?.trim()) {
      return { field: "courseTitle", message: "Course title is required." };
    }
    if (values.courseTitle.trim().length < 10) {
      return {
        field: "courseTitle",
        message: "Course title must be at least 10 characters.",
      };
    }
    if (!selectedCategoryId) {
      return { field: "selectedCategoryId", message: "Course category is required." };
    }
    if (!selectedLevelId) {
      return { field: "selectedLevelId", message: "Level is required." };
    }
    if (!values.price?.trim()) {
      return { field: "price", message: "Price is required." };
    }
    if (!selectedSubCategoryId) {
      return { field: "selectedSubCategoryId", message: "Sub category is required." };
    }
    if (!selectedCourseType) {
      return { field: "selectedCourseType", message: "Course type is required." };
    }
    if (!outlineFile) {
      return { field: "outlineFile", message: "Course outline is required." };
    }
    if (!selectedAgeGroupId) {
      return { field: "selectedAgeGroupId", message: "Age group is required." };
    }
    if (!values.accessDuration?.trim()) {
      return { field: "accessDuration", message: "Access duration is required." };
    }
    if (
      !/^\d+$/.test(values.accessDuration.trim()) ||
      Number(values.accessDuration.trim()) <= 0
    ) {
      return {
        field: "accessDuration",
        message: "Access duration must be a number greater than 0.",
      };
    }

    return null;
  };

  const [createCourse, { isLoading: isCreating }] = useCreateCourseMutation();

  const { data: singleCategoryData, isFetching: isCategoryDetailsLoading } =
    useGetSingleCategoryAdminQuery(selectedCategoryId, {
      skip: !selectedCategoryId,
    });

  const selectedCategory = selectedCategoryId
    ? singleCategoryData?.data || null
    : null;

  const categoryOptions = categories.map((category) => ({
    label: category.categoryName,
    value: category._id,
  }));

  const levelOptions = (selectedCategory?.categoryLevel || []).map((level) => ({
    label: level.title || level.name,
    value: level._id,
  }));

  const ageGroupOptions = (selectedCategory?.categoryAgeGroup || []).map(
    (ageGroup) => ({
      label: ageGroup.title || ageGroup.name,
      value: ageGroup._id,
    }),
  );

  const subCategoryOptions = (selectedCategory?.subCategory || []).map(
    (subCategory) => ({
      label: subCategory.title || subCategory.name,
      value: subCategory._id,
    }),
  );

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const formValues = getValues();
    const validationError = getFirstFieldError(formValues);

    if (validationError) {
      setFieldError(validationError);
      focusField(validationError.field);
      return;
    }

    setFieldError(null);
    await onSubmit(formValues);
  };

  const onSubmit = async (formValues) => {
    try {
      setApiError("");

      const formData = new FormData();

      formData.append("courseTitle", formValues.courseTitle);
      formData.append("coursePrice", formValues.price);
      formData.append("courseAccess", formValues.accessDuration);

      formData.append("courseCategory", selectedCategoryId);
      formData.append("courseLevel", selectedLevelId);
      formData.append("courseSubCategory", selectedSubCategoryId);
      formData.append("courseAgeGroup", selectedAgeGroupId);
      formData.append("courseEnrollementType", selectedCourseType);

      if (thumbnailFile) {
        formData.append("files", thumbnailFile);
      }

      if (outlineFile) {
        formData.append("files", outlineFile);
      }

      const response = await createCourse(formData).unwrap();

      showToast({
        message: response?.message || "Course created successfully!",
        type: "success",
      });

      reset();
      setSelectedCategoryId("");
      setSelectedLevelId("");
      setSelectedSubCategoryId("");
      setSelectedAgeGroupId("");
      setSelectedCourseType("");
      setThumbnailFile(null);
      setOutlineFile(null);
      setFieldError(null);

      onClose();
    } catch (error) {
      console.error("CREATE COURSE ERROR:", error);

      const errorMessage =
        error?.data?.message ||
        "Something went wrong while creating the course.";

      setApiError(errorMessage);
      showToast({
        message: errorMessage,
        type: "error",
      });
    }
  };

  const modalRef = useClickOutsideModal(onClose);
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/55 px-4 py-6">
      <div
        ref={modalRef}
        className="w-full max-w-[1126px] overflow-visible rounded-[30px] bg-white"
      >
        <div className="flex max-w-[1100px] items-center justify-between px-6 py-8">
          <h3 className="text-[28px] font-bold leading-[33px] text-[#000]">
            Add Course
          </h3>

          <button
            type="button"
            aria-label="Close add course modal"
            onClick={onClose}
            className="rounded-full p-1 text-[#000] transition hover:bg-black/5"
          >
            <X size={20} />
          </button>
        </div>

        <form
          className="max-h-[calc(100vh-10rem)] space-y-6 overflow-y-auto border-t border-[#00000080] px-12 pt-8 pb-9"
          onSubmit={handleFormSubmit}
          noValidate
        >
          {/* Course Thumbnail */}
          <div>
            <p className="mb-3 text-[22px] font-medium leading-[26px] text-[#000]">
              Course Thumbnail
            </p>

            <label
              ref={registerFieldRef("thumbnailFile")}
              tabIndex={-1}
              className={`flex min-h-[176px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-4 text-center outline-none ${
                fieldError?.field === "thumbnailFile"
                  ? "border-red-500 focus:ring-2 focus:ring-red-400"
                  : "border-[#00000080] focus:ring-2 focus:ring-[#923D8C]"
              }`}
            >
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  setThumbnailFile(file || null);
                  if (file) {
                    clearFieldError("thumbnailFile");
                  }
                }}
              />

              {thumbnailFile ? (
                <>
                  {/* Image preview */}
                  <div className="mb-3 flex h-[90px] w-[160px] items-center justify-center overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]">
                    <img
                      src={URL.createObjectURL(thumbnailFile)}
                      alt="Course thumbnail preview"
                      className="h-full w-full object-contain"
                    />
                  </div>

                  {/* File name */}
                  <p
                    className="w-full max-w-[280px] truncate text-base font-medium text-[#374151]"
                    title={thumbnailFile.name}
                  >
                    {thumbnailFile.name}
                  </p>

                  <p className="mt-1 text-sm text-[#923D8C]">
                    Click to change image
                  </p>
                </>
              ) : (
                <>
                  <span className="mb-4 rounded-full bg-[#923D8C33] p-3.5">
                    <AddImageIcon />
                  </span>

                  <p className="text-xl font-medium leading-[23px] text-[#9C9C9C]">
                    Drag & drop your image here or{" "}
                    <span className="font-semibold text-[#923D8C]">browse</span>
                  </p>

                  <p className="mt-2 leading-[19px] text-[#9c9c9c]">
                    Supported formats: JPG, PNG
                  </p>
                </>
              )}
            </label>
            {fieldError?.field === "thumbnailFile" && (
              <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-x-16 gap-y-4 md:grid-cols-2">
            <div>
              <p className="mb-3 text-[22px] font-medium leading-[26px] text-[#000]">
                Course Title
              </p>

              <input
                {...courseTitleField}
                ref={(element) => {
                  courseTitleField.ref(element);
                  registerFieldRef("courseTitle")(element);
                }}
                onChange={(event) => {
                  courseTitleField.onChange(event);
                  clearFieldError("courseTitle");
                }}
                placeholder="Enter course title"
                className={`h-[52px] w-full rounded-lg border px-6 text-xl outline-none ${
                  fieldError?.field === "courseTitle"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
              />
              {fieldError?.field === "courseTitle" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <FormSelect
                id="course-level"
                options={levelOptions}
                label="Level"
                labelClassName="mb-3 text-[22px] font-medium leading-[26px]"
                placeholder={
                  isCategoryDetailsLoading
                    ? "Loading..."
                    : selectedCategory
                      ? "Select Level"
                      : "Select Category First"
                }
                emptyByDefault
                className="w-full"
                controlWrapperClassName="mt-0"
                selectClassName={`h-[52px] capitalize w-full rounded-lg border px-6 text-xl ${
                  fieldError?.field === "selectedLevelId"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
                dropdownClassName="left-0 w-full max-h-60 overflow-y-auto rounded-lg border border-[#00000080] bg-white"
                optionClassName="capitalize px-4 py-2 text-lg text-[#111111]"
                icon={DropDownBold}
                iconProps={{ color: "#000000" }}
                onChange={(value) => {
                  setSelectedLevelId(value);
                  clearFieldError("selectedLevelId");
                }}
              />
              {fieldError?.field === "selectedLevelId" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <FormSelect
                id="course-category"
                options={categoryOptions}
                placeholder="Select Category"
                label="Course Category"
                labelClassName="mb-3 text-[22px] font-medium leading-[26px]"
                emptyByDefault
                className="w-full"
                controlWrapperClassName="mt-0"
                selectClassName={`h-[52px] w-full rounded-lg border px-6 text-xl capitalize ${
                  fieldError?.field === "selectedCategoryId"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
                dropdownClassName="left-0 w-full max-h-60 overflow-y-auto rounded-lg border border-[#00000080] bg-white"
                optionClassName="px-4 py-2 text-lg text-[#111111] capitalize"
                icon={DropDownBold}
                iconProps={{ color: "#000000" }}
                onChange={(value) => {
                  setSelectedCategoryId(value);
                  setSelectedLevelId("");
                  setSelectedSubCategoryId("");
                  setSelectedAgeGroupId("");
                  clearFieldError("selectedCategoryId");
                }}
              />
              {fieldError?.field === "selectedCategoryId" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <p className="mb-3 text-[22px] font-medium leading-[26px] text-[#000]">
                Price
              </p>

              <input
                inputMode="decimal"
                placeholder="Add Price"
                {...priceField}
                ref={(element) => {
                  priceField.ref(element);
                  registerFieldRef("price")(element);
                }}
                onChange={(event) => {
                  priceField.onChange(event);
                  clearFieldError("price");
                }}
                className={`h-[52px] w-full rounded-lg border px-6 text-xl outline-none ${
                  fieldError?.field === "price"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
              />
              {fieldError?.field === "price" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <FormSelect
                id="course-sub-category"
                label="Sub Category"
                labelClassName="mb-3 text-[22px] font-medium leading-[26px]"
                options={subCategoryOptions}
                placeholder={
                  isCategoryDetailsLoading
                    ? "Loading..."
                    : selectedCategory
                      ? "Select Sub Category"
                      : "Select Category First"
                }
                emptyByDefault
                className="w-full"
                controlWrapperClassName="mt-0"
                selectClassName={`h-[52px] capitalize w-full rounded-lg border px-6 text-xl ${
                  fieldError?.field === "selectedSubCategoryId"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
                dropdownClassName="left-0 w-full max-h-60 overflow-y-auto rounded-lg border border-[#00000080] bg-white"
                optionClassName="capitalize px-4 py-2 text-lg text-[#111111]"
                icon={DropDownBold}
                iconProps={{ color: "#000" }}
                onChange={(value) => {
                  setSelectedSubCategoryId(value);
                  clearFieldError("selectedSubCategoryId");
                }}
              />
              {fieldError?.field === "selectedSubCategoryId" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <FormSelect
                id="course-type"
                label="Course Type"
                labelClassName="mb-3 text-[22px] font-medium leading-[26px]"
                options={[
                  {
                    label: "Live Session",
                    value: "live",
                  },
                  {
                    label: "Recorded Session",
                    value: "recorded",
                  },
                ]}
                placeholder="Select Course Type"
                emptyByDefault
                className="w-full"
                controlWrapperClassName="mt-0"
                selectClassName={`h-[52px] w-full rounded-lg border px-6 text-xl ${
                  fieldError?.field === "selectedCourseType"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
                dropdownClassName="left-0 w-full max-h-60 overflow-y-auto rounded-lg border border-[#00000080] bg-white"
                optionClassName="px-4 py-2 text-lg text-[#111111]"
                icon={DropDownBold}
                iconProps={{ color: "#000" }}
                onChange={(value) => {
                  setSelectedCourseType(value);
                  clearFieldError("selectedCourseType");
                }}
              />
              {fieldError?.field === "selectedCourseType" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <p className="mb-3 text-[22px] font-medium leading-[26px] text-[#000]">
                Course Outline
              </p>

              <label
                ref={registerFieldRef("outlineFile")}
                tabIndex={-1}
                className={`flex min-h-[176px] cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-4 text-center outline-none ${
                  fieldError?.field === "outlineFile"
                    ? "border-red-500 focus:ring-2 focus:ring-red-400"
                    : "border-[#00000080] focus:ring-2 focus:ring-[#923D8C]"
                }`}
              >
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setOutlineFile(file || null);
                    if (file) {
                      clearFieldError("outlineFile");
                    }
                  }}
                  className="sr-only"
                />

                {outlineFile ? (
                  <>
                    {/* PDF icon */}
                    <span className="mb-4 rounded-full bg-[#923D8C33] px-[19.5px] py-[16.5px]">
                      <File />
                    </span>

                    {/* File name */}
                    <p
                      className="w-full max-w-[320px] truncate text-base font-medium text-[#374151]"
                      title={outlineFile.name}
                    >
                      {outlineFile.name}
                    </p>

                    {/* Change file */}
                    <p className="mt-1 text-sm text-[#923D8C]">
                      Click to change PDF
                    </p>
                  </>
                ) : (
                  <>
                    <span className="mb-4 rounded-full bg-[#923D8C33] px-[19.5px] py-[16.5px]">
                      <File />
                    </span>

                    <p className="text-xl font-medium leading-[23px] text-[#9C9C9C]">
                      Drag & drop your PDF here or{" "}
                      <span className="font-semibold text-[#923D8C]">
                        browse
                      </span>
                    </p>

                    <p className="mt-2 leading-[19px] text-[#9C9C9C]">
                      Only PDF format is supported
                    </p>
                  </>
                )}
              </label>
              {fieldError?.field === "outlineFile" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}
            </div>

            <div>
              <FormSelect
                id="course-age-group"
                label="Age Group"
                labelClassName="mb-3 text-[22px] font-medium leading-[26px]"
                options={ageGroupOptions}
                placeholder={
                  isCategoryDetailsLoading
                    ? "Loading..."
                    : selectedCategory
                      ? "Select Age Group"
                      : "Select Category First"
                }
                emptyByDefault
                className="w-full"
                controlWrapperClassName="mt-0"
                selectClassName={`h-[52px] capitalize w-full rounded-lg border px-6 text-xl ${
                  fieldError?.field === "selectedAgeGroupId"
                    ? "border-red-500"
                    : "border-[#00000080]"
                }`}
                dropdownClassName="left-0 w-full max-h-60 overflow-y-auto rounded-lg border border-[#00000080] bg-white"
                optionClassName="capitalize px-4 py-2 text-lg text-[#111111]"
                icon={DropDownBold}
                iconProps={{ color: "#000" }}
                onChange={(value) => {
                  setSelectedAgeGroupId(value);
                  clearFieldError("selectedAgeGroupId");
                }}
              />
              {fieldError?.field === "selectedAgeGroupId" && (
                <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
              )}

              <div className="mt-5">
                <p className="mb-3 text-[22px] font-medium leading-[26px] text-[#000]">
                  Access(Duration)
                </p>

                <input
                  {...accessDurationField}
                  ref={(element) => {
                    accessDurationField.ref(element);
                    registerFieldRef("accessDuration")(element);
                  }}
                  inputMode="numeric"
                  onChange={(event) => {
                    event.target.value = event.target.value.replace(/\D/g, "");
                    accessDurationField.onChange(event);
                    clearFieldError("accessDuration");
                  }}
                  placeholder="Enter duration"
                  className={`h-[52px] w-full rounded-lg border px-6 text-xl outline-none ${
                    fieldError?.field === "accessDuration"
                      ? "border-red-500"
                      : "border-[#00000080]"
                  }`}
                />
                {fieldError?.field === "accessDuration" && (
                  <p className="mt-2 text-sm text-red-500">{fieldError.message}</p>
                )}
              </div>

              {apiError && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {apiError}
                </div>
              )}

              <div className="mt-[22px] flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-[4px] border border-[#6B7280B2] bg-[#EDEDED] px-[17px] py-[7px] font-['Inter'] leading-6 text-[#4A4A68]"
                >
                  Cancel
                </button>

                <button
                  disabled={isCreating}
                  className="rounded-[4px] bg-[#923D8C] px-[17px] py-2 font-['Inter'] leading-6 text-white"
                >
                  {isCreating ? "Creating..." : "Create Course"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCourseModal;
